// QuickBooksContext.js
import React, {
  createContext,
  useState,
  useEffect,
  useRef,
  useContext,
  useCallback,
} from "react";
import axios from "axios";

const QuickBooksContext = createContext(null);

// Constants for timing configuration
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes cache
const VERIFICATION_INTERVAL = 15 * 60 * 1000; // Check every 15 minutes instead of 10
const MAX_RANDOM_DELAY = 3000; // Reduce random delay max from 5000ms to 3000ms

// Local storage keys
const STORAGE_KEYS = {
  CONNECTION_STATUS: "qb_connection_status",
  LAST_VERIFIED: "qb_last_verified",
};

export function QuickBooksProvider({ children }) {
  const [isConnected, setIsConnected] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(
    !!localStorage.getItem("token")
  );

  // Use refs to avoid unnecessary re-renders and track state between renders
  const verificationTimeout = useRef(null);
  const verificationIntervalId = useRef(null);
  const pendingVerification = useRef(false);

  // Initialize cached values from localStorage
  const lastVerifiedRef = useRef(
    parseInt(localStorage.getItem(STORAGE_KEYS.LAST_VERIFIED)) || null
  );

  // On mount, try to restore connection status from localStorage
  useEffect(() => {
    try {
      const storedStatus = localStorage.getItem(STORAGE_KEYS.CONNECTION_STATUS);
      if (storedStatus) {
        const parsedStatus = JSON.parse(storedStatus);
        if (parsedStatus && typeof parsedStatus.connected === "boolean") {
          setIsConnected(parsedStatus.connected);
        }
      }
    } catch (err) {
      console.error("Error loading cached QuickBooks connection status:", err);
    }
  }, []);

  // Check authentication status whenever localStorage changes
  useEffect(() => {
    const checkAuthStatus = () => {
      const hasToken = !!localStorage.getItem("token");
      setIsAuthenticated(hasToken);

      // If logged out, also reset connection status
      if (!hasToken && isConnected) {
        setIsConnected(false);
        localStorage.removeItem(STORAGE_KEYS.CONNECTION_STATUS);
        localStorage.removeItem(STORAGE_KEYS.LAST_VERIFIED);
      }
    };

    // Check immediately
    checkAuthStatus();

    // Also listen for storage events (for when token is removed in another tab)
    window.addEventListener("storage", checkAuthStatus);

    return () => {
      window.removeEventListener("storage", checkAuthStatus);
    };
  }, [isConnected]);

  // Save connection status to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEYS.CONNECTION_STATUS,
        JSON.stringify({
          connected: isConnected,
          timestamp: Date.now(),
        })
      );
    } catch (err) {
      console.error("Error saving QuickBooks connection status:", err);
    }
  }, [isConnected]);

  // Function to verify the connection - wrapped in useCallback
  const verifyConnection = useCallback(
    async (force = false) => {
      // Don't verify if not authenticated
      if (!isAuthenticated) {
        return false;
      }

      // Prevent multiple simultaneous verification requests
      if (pendingVerification.current && !force) {
        return isConnected;
      }

      // Don't verify too frequently unless forced
      const now = Date.now();
      if (
        !force &&
        lastVerifiedRef.current &&
        now - lastVerifiedRef.current < CACHE_DURATION
      ) {
        return isConnected;
      }

      try {
        // Mark verification as pending
        pendingVerification.current = true;

        console.log("Verifying QuickBooks connection...");

        // IMPORTANT: This is the direct approach that worked before
        const response = await axios.get("/api/quickbooks/verify-connection", {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });

        console.log("Verification result:", response.data);

        // Update timestamp
        lastVerifiedRef.current = now;

        // IMPORTANT: Always update the state based on the response
        // Don't check if it changed - just set it
        setIsConnected(response.data.connected);

        // Also update localStorage
        localStorage.setItem(
          STORAGE_KEYS.CONNECTION_STATUS,
          JSON.stringify({
            connected: response.data.connected,
            timestamp: now,
          })
        );
        localStorage.setItem(STORAGE_KEYS.LAST_VERIFIED, now.toString());

        return response.data.connected;
      } catch (error) {
        console.error("Error verifying QuickBooks connection:", error);
        setIsConnected(false);
        return false;
      } finally {
        pendingVerification.current = false;
      }
    },
    [isAuthenticated, isConnected]
  );

  // Add this effect to detect OAuth returns - THIS IS THE IMPORTANT NEW PART
  useEffect(() => {
    // Check for realmId parameter which QuickBooks adds when returning from OAuth
    const checkForOAuthReturn = () => {
      console.log("Checking for OAuth return parameters");

      const urlParams = new URLSearchParams(window.location.search);
      console.log("URL params:", Object.fromEntries(urlParams.entries()));

      if (
        urlParams.has("realmId") ||
        urlParams.has("code") ||
        urlParams.has("state")
      ) {
        console.log("Detected QuickBooks OAuth return, forcing verification");
        // Force immediate verification
        setTimeout(() => {
          verifyConnection(true);
        }, 500); // Add small delay to ensure everything is initialized
      }

      // Also check if we're returning from connection process based on localStorage flag
      if (localStorage.getItem("qb_connecting") === "true") {
        console.log(
          "Detected QuickBooks connection flag, forcing verification"
        );
        localStorage.removeItem("qb_connecting");
        setTimeout(() => {
          verifyConnection(true);
        }, 500);
      }
    };

    if (isAuthenticated) {
      checkForOAuthReturn();
    }
  }, [isAuthenticated, verifyConnection]);

  // Set up and tear down the verification interval based on authentication status
  useEffect(() => {
    // Only set up verification if authenticated
    if (isAuthenticated) {
      // Initial verification - only if we haven't verified recently
      if (
        !lastVerifiedRef.current ||
        Date.now() - lastVerifiedRef.current > CACHE_DURATION
      ) {
        verifyConnection(true);
      }

      // Set up interval for periodic checks - less frequent
      verificationIntervalId.current = setInterval(() => {
        // Clear any pending verification
        if (verificationTimeout.current) {
          clearTimeout(verificationTimeout.current);
        }

        // Only schedule a new verification if we haven't verified recently
        if (
          !lastVerifiedRef.current ||
          Date.now() - lastVerifiedRef.current > CACHE_DURATION
        ) {
          // Set new verification with slight random delay to prevent bunching
          const randomDelay = Math.floor(Math.random() * MAX_RANDOM_DELAY);
          verificationTimeout.current = setTimeout(() => {
            // Only verify if not already verifying
            if (!pendingVerification.current) {
              verifyConnection();
            }
          }, randomDelay);
        }
      }, VERIFICATION_INTERVAL);
    } else {
      // Clean up interval if not authenticated
      if (verificationIntervalId.current) {
        clearInterval(verificationIntervalId.current);
        verificationIntervalId.current = null;
      }

      if (verificationTimeout.current) {
        clearTimeout(verificationTimeout.current);
        verificationTimeout.current = null;
      }

      // Reset connection status
      setIsConnected(false);
    }

    // Cleanup on unmount
    return () => {
      if (verificationIntervalId.current) {
        clearInterval(verificationIntervalId.current);
      }
      if (verificationTimeout.current) {
        clearTimeout(verificationTimeout.current);
      }
    };
  }, [isAuthenticated, verifyConnection]);

  const value = {
    isConnected,
    isAuthenticated,
    setIsConnected,
    verifyConnection,
  };

  return (
    <QuickBooksContext.Provider value={value}>
      {children}
    </QuickBooksContext.Provider>
  );
}

export function useQuickBooks() {
  const context = useContext(QuickBooksContext);
  if (!context) {
    throw new Error("useQuickBooks must be used within a QuickBooksProvider");
  }
  return context;
}
